import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Network } from "lucide-react";
import { EmptyState } from "@/components/looseend/EmptyState";
import { HostedLoopsNotice } from "@/components/looseend/HostedLoopsNotice";
import { LoopStatusBadge } from "@/components/looseend/LoopStatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useInspector } from "@/components/looseend/inspector-context";
import { activeLoops } from "@/lib/looseend/selectors";
import { isOverdue, relativeDay } from "@/lib/looseend/format";
import { cn } from "@/lib/utils";
import { STATUS_META } from "@/lib/looseend/status";
import { useAppState } from "@/lib/looseend/store";
import { useHostedLoops } from "@/lib/loops/product/hostedLoops";
import { PEE_ARIA } from "@/lib/looseend/pee";

export const Route = createFileRoute("/app/chaos-map")({
  head: () => ({
    meta: [
      { title: "Chaos Map — L∞seEnd" },
      {
        name: "description",
        content:
          "See where your unresolved weight is clustered, grouped by the people and areas holding it.",
      },
      { property: "og:title", content: "Chaos Map — L∞seEnd" },
      { property: "og:description", content: "A visual read on your open Loops and their weight." },
    ],
  }),
  component: ChaosMapPage,
});

function ChaosMapPage() {
  const state = useAppState();
  const { open } = useInspector();
  const [grouping, setGrouping] = useState<"cluster" | "status">("cluster");

  const hosted = useHostedLoops();
  const loops = useMemo(() => activeLoops([...hosted.loops]), [hosted.loops]);

  const groups = useMemo(() => {
    const map = new Map<string, typeof loops>();
    for (const loop of loops) {
      const key = grouping === "cluster" ? loop.cluster : STATUS_META[loop.status].label;
      map.set(key, [...(map.get(key) ?? []), loop]);
    }
    return Array.from(map.entries())
      .map(([label, items]) => ({
        label,
        items: items.slice().sort((a, b) => b.weight - a.weight),
        weight: items.reduce((sum, l) => sum + l.weight, 0),
      }))
      .sort((a, b) => b.weight - a.weight);
  }, [loops, grouping]);

  const maxWeight = Math.max(1, ...loops.map((l) => l.weight));

  return (
    <div className="h-full overflow-y-auto px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-4xl">
        <header className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 sm:flex sm:flex-wrap sm:justify-between">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight text-foreground">
              Chaos Map
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              See what’s unresolved and where your attention may be needed.
            </p>
            <p className="mt-1 text-xs text-muted-foreground/80">
              {loops.length} unresolved · bigger circles carry more weight · grouped by{" "}
              <span aria-label={PEE_ARIA}>PEE™</span>
            </p>
          </div>
          <Select value={grouping} onValueChange={(v) => setGrouping(v as "cluster" | "status")}>
            <SelectTrigger className="w-[170px] shrink-0" aria-label="Group Loops by">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cluster">By area of life</SelectItem>
              <SelectItem value="status">By what’s happening</SelectItem>
            </SelectContent>
          </Select>
        </header>

        <HostedLoopsNotice
          phase={hosted.phase}
          message={hosted.message}
          onRetry={hosted.refresh}
          rows={2}
        />

        {hosted.phase !== "ready" ? null : loops.length === 0 ? (
          <EmptyState
            className="mt-6"
            icon={<Network className="size-6" aria-hidden="true" />}
            title="Nothing is being tracked yet."
            description="Tell LooseEnd what’s hanging over you and it will appear here."
          />
        ) : (
          <div className="mt-6 space-y-4">
            {groups.map((group) => (
              <section key={group.label} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h2 className="text-sm font-semibold text-foreground">{group.label}</h2>
                  <p className="text-xs text-muted-foreground">
                    {group.items.length} unresolved
                    {group.items.some(isOverdue)
                      ? ` · ${group.items.filter(isOverdue).length} overdue`
                      : ""}
                  </p>
                </div>
                <ul className="mt-4 flex flex-wrap items-end gap-3">
                  {group.items.map((loop) => {
                    const scale = 76 + Math.round((loop.weight / maxWeight) * 68);
                    return (
                      <li key={loop.id}>
                        <button
                          type="button"
                          onClick={() => open(loop.id)}
                          style={{ width: scale, height: scale }}
                          title={`${loop.title} — ${STATUS_META[loop.status].label}`}
                          aria-label={`${loop.title}. ${isOverdue(loop) ? "Overdue. " : ""}${STATUS_META[loop.status].label}.`}
                          className="grid place-items-center rounded-full border border-border bg-surface p-2 text-center transition-transform hover:scale-105 hover:border-primary/40 focus-visible:scale-105"
                        >
                          <span className="line-clamp-3 text-[11px] font-medium leading-tight text-foreground">
                            {loop.title}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <ul className="mt-4 space-y-1.5">
                  {group.items.slice(0, 3).map((loop) => (
                    <li
                      key={loop.id}
                      className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
                    >
                      <LoopStatusBadge status={loop.status} size="sm" />
                      <span className="min-w-0 truncate text-foreground">{loop.title}</span>
                      {loop.expectedDate ? (
                        <span
                          className={cn(
                            "ml-auto shrink-0",
                            isOverdue(loop) && "text-status-action",
                          )}
                        >
                          {isOverdue(loop) ? "Overdue" : relativeDay(loop.expectedDate)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
